import { useState } from 'react';
import { FileText, Search } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useInvoiceActionMutation, useInvoicesQuery, type Invoice,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { ALL_TIME, DateRangeFilter, type DateRange } from '@/components/DateRangeFilter';

const STATUS_TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'brand'> = {
  DRAFT: 'neutral',
  ISSUED: 'brand',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  OVERDUE: 'danger',
  VOID: 'neutral',
};

export function InvoicesPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [range, setRange] = useState<DateRange>(ALL_TIME);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'overdue' | 'unpaid'>('all');
  const [voiding, setVoiding] = useState<Invoice | null>(null);
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useInvoicesQuery({
    from: range.from || undefined,
    to: range.to || undefined,
    page,
    limit: 25,
    search: search || undefined,
    overdueOnly: filter === 'overdue' || undefined,
    status: filter === 'unpaid' ? 'ISSUED' : undefined,
  });
  const [runAction, actionState] = useInvoiceActionMutation();

  const act = async (id: string, action: string, body?: Record<string, unknown>): Promise<void> => {
    setFailure(null);
    try {
      await runAction({ id, action, body }).unwrap();
      setVoiding(null);
      setReason('');
    } catch (actionError) {
      setFailure(apiErrorMessage(actionError, `That invoice could not be ${action}ed`));
    }
  };

  return (
    <>
      <PageHeader
        title="Invoices"
        description="The document the customer receives. Money lives on the sale behind it."
      />

      {failure && (
        <div className="mb-4">
          <Alert tone="danger">{failure}</Alert>
        </div>
      )}

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Invoices', value: String(data.summary.count), hint: 'in this period' },
            { label: 'Invoiced', value: formatMoney(String(data.summary.invoiced), format) },
            { label: 'Collected', value: formatMoney(String(data.summary.collected), format) },
            {
              label: 'Outstanding',
              value: formatMoney(String(data.summary.outstanding), format),
              hint: 'still owed',
              tone: 'owed',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <DateRangeFilter
            value={range}
            onChange={(next) => {
              setRange(next);
              setPage(1);
            }}
          />
          <div className="min-w-48 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Invoice number"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              label="Show"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value as typeof filter);
                setPage(1);
              }}
            >
              <option value="all">All invoices</option>
              <option value="unpaid">Still owing</option>
              <option value="overdue">Overdue</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={filter === 'all' ? 'No invoices yet' : 'Nothing matches'}
            description="Invoices are issued from a completed sale."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>Customer</Th>
                  <Th>Sale</Th>
                  <Th>Due</Th>
                  <Th numeric>Total</Th>
                  <Th numeric>Outstanding</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-sunken">
                    <Td className="font-medium">{invoice.number}</Td>
                    <Td className="text-secondary">{invoice.customer?.name ?? 'Walk-in'}</Td>
                    <Td className="text-secondary">{invoice.sale?.number ?? '—'}</Td>
                    <Td className="text-secondary">
                      {invoice.dueDate ? formatDate(invoice.dueDate, format) : '—'}
                    </Td>
                    <Td numeric>{formatMoney(invoice.total, format)}</Td>
                    <Td
                      numeric
                      className={Number(invoice.dueAmount) > 0 ? 'text-red-600' : 'text-secondary'}
                    >
                      {formatMoney(invoice.dueAmount, format)}
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[invoice.status] ?? 'neutral'}>
                        {invoice.status.toLowerCase()}
                      </Badge>
                    </Td>
                    <Td className="text-right">
                      {invoice.status !== 'VOID' && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={actionState.isLoading}
                            onClick={() => void act(invoice.id, 'refresh')}
                            title="Re-read the sale's payment position"
                          >
                            Refresh
                          </Button>
                          <Can permission={P.INVOICE_ISSUE}>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setFailure(null);
                                setVoiding(invoice);
                              }}
                            >
                              Void
                            </Button>
                          </Can>
                        </>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={voiding !== null}
        onClose={() => setVoiding(null)}
        title="Void this invoice?"
        description="The sale, its stock and the customer's balance are untouched — cancelling what was sold is a return, not a reprint."
        footer={
          <>
            <Button variant="outline" onClick={() => setVoiding(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={actionState.isLoading}
              disabled={!reason}
              onClick={() => voiding && void act(voiding.id, 'void', { reason })}
            >
              Void invoice
            </Button>
          </>
        }
      >
        <Input
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </>
  );
}
